import React, { lazy, Suspense, useState } from 'react';
import PropTypes from 'prop-types';
import { hasValue } from 'globalutils/fmUtils';
import { getCurrentDriverLabel } from './udReassignmentUtils';

const layout = window?.$environment?.CURRENT_LAYOUT || 'SpireonNew';
const ModalModule = require(`globalcomponents/UI/Modal/${layout}`);
const ModalComponent = ModalModule?.default || ModalModule;
const SuccessIcon = lazy(() => import(`globalcomponents/SuccessIcon/${layout}`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));

const REASSIGN_API_ID = 'createUdReassignment';

const UdReassignmentFormContent = ({
  entry,
  companyDrivers,
  logIdPub,
  i18n,
  createUdReassignment,
  getDailyLogDetails,
  fetchDailyLogDetailsApiId,
  hasReassignApiStatus,
  handleClose,
  setSuccessOpen,
  setOpen,
}) => {
  const [annotation, setAnnotation] = useState('');
  const isRequestEnabled = annotation.trim().length > 0;

  const handleSubmit = () => {
    if (!isRequestEnabled || hasReassignApiStatus?.isLoading) return;

    const payloadBody = {
      entryId: entry?.id ?? entry?.entryId,
      eventId: entry?.eventId || '',
      minutesFrom: entry?.minutes_from,
      minutesTo: entry?.minutes_to,
      annotation: annotation.trim(),
    };

    console.log('UD Reassignment Request Body Sent to API:', payloadBody);

    createUdReassignment({
      apiId: REASSIGN_API_ID,
      data: {
        id: logIdPub,
        body: payloadBody,
      },
      callback: () => {
        setOpen(false);
        setSuccessOpen(true);
        getDailyLogDetails({
          data: { id: logIdPub },
          apiId: fetchDailyLogDetailsApiId,
        });
        setAnnotation('');
      },
    });
  };

  return (
    <div className="udReassignModalWrapper">
      <style>{`
        .udReassignModalWrapper {
          width: 460px;
          max-width: 90vw;
          padding: 24px 28px;
          background: #ffffff;
          font-family: inherit;
          color: #111111;
          box-sizing: border-box;
          text-align: left;
        }
        .udReassignModalWrapper h2.modalTitle {
          font-size: 20px;
          font-weight: 700;
          margin: 0 0 20px 0;
          color: #000000;
        }
        .udReassignSection {
          margin-bottom: 20px;
        }
        .udReassignSectionTitle {
          font-size: 15px;
          font-weight: 700;
          color: #000000;
          margin: 0 0 12px 0;
        }
        .udReassignHeaderRow {
          display: flex;
          align-items: center;
          margin-bottom: 12px;
          font-size: 14px;
          font-weight: 700;
          color: #000000;
        }
        .udReassignGrid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          row-gap: 14px;
          column-gap: 20px;
          margin-bottom: 12px;
        }
        .udReassignGridItemFull {
          grid-column: span 2;
        }
        .udReassignFieldLabel {
          font-size: 13px;
          color: #555555;
          margin-bottom: 3px;
        }
        .udReassignFieldValue {
          font-size: 14px;
          font-weight: 500;
          color: #000000;
        }
        .udReassignFormRow {
          display: flex;
          align-items: center;
          margin-bottom: 16px;
        }
        .udReassignFormRow.udReassignFormRowTextarea {
          align-items: flex-start;
        }
        .udReassignFormLabel {
          width: 110px;
          min-width: 110px;
          font-size: 14px;
          color: #111111;
          padding-top: 4px;
        }
        .udReassignFormInputWrapper {
          flex: 1;
        }
        .udReassignStaticField {
          width: 100%;
          padding: 8px 12px;
          border-bottom: 1px solid #d9d9d9;
          background: #ffffff;
          font-size: 14px;
          color: #333;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-sizing: border-box;
        }
        .udReassignTextarea {
          width: 100%;
          min-height: 54px;
          padding: 8px 12px;
          border: none;
          border-bottom: 1px solid #d9d9d9;
          font-size: 14px;
          font-family: inherit;
          outline: none;
          resize: vertical;
          box-sizing: border-box;
        }
        .udReassignTextarea:focus {
          border-bottom-color: #0075ff;
        }
        .udReassignFooterActions {
          display: flex;
          justify-content: center;
          gap: 16px;
          margin-top: 24px;
        }
        .udReassignBtnCancel {
          padding: 8px 24px;
          background: #ffffff;
          border: 1px solid #d9d9d9;
          border-radius: 2px;
          font-size: 13px;
          font-weight: 600;
          color: #333333;
          cursor: pointer;
          text-transform: uppercase;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }
        .udReassignBtnCancel:hover {
          background: #f5f5f5;
        }
        .udReassignBtnSubmit {
          padding: 8px 24px;
          background: #0075ff;
          border: none;
          border-radius: 2px;
          font-size: 13px;
          font-weight: 600;
          color: #ffffff;
          cursor: pointer;
          text-transform: uppercase;
          box-shadow: 0 1px 2px rgba(0,0,0,0.1);
        }
        .udReassignBtnSubmit:disabled {
          background: #96c4ff;
          cursor: not-allowed;
        }
        .udReassignBtnSubmit:not(:disabled):hover {
          background: #0060d0;
        }
      `}</style>

      <h2 className="modalTitle">{i18n.t('logs.reassignToUdHeading')}</h2>

      <div className="udReassignSection">
        <h3 className="udReassignSectionTitle">{i18n.t('logs.reassignEventDetails') || 'Event details'}</h3>

        <div className="udReassignHeaderRow">
          <span>{entry?.status || 'DRIVING'}</span>
        </div>

        <div className="udReassignGrid">
          <div>
            <div className="udReassignFieldLabel">{i18n.t('logs.timeFrom') || 'Time From'}</div>
            <div className="udReassignFieldValue">{entry?.time_from || '-'}</div>
          </div>
          <div>
            <div className="udReassignFieldLabel">{i18n.t('logs.timeTo') || 'Time To'}</div>
            <div className="udReassignFieldValue">{entry?.time_to || '-'}</div>
          </div>
          <div>
            <div className="udReassignFieldLabel">{i18n.t('logs.location') || 'Location'}</div>
            <div className="udReassignFieldValue">{entry?.location || '-'}</div>
          </div>
          <div>
            <div className="udReassignFieldLabel">{i18n.t('logs.notes') || 'Notes'}</div>
            <div className="udReassignFieldValue">{entry?.note || '-'}</div>
          </div>
          <div className="udReassignGridItemFull">
            <div className="udReassignFieldLabel">{i18n.t('logs.assignedDriver') || 'Current Driver'}</div>
            <div className="udReassignFieldValue">{getCurrentDriverLabel(entry, companyDrivers)}</div>
          </div>
        </div>
      </div>

      <div className="udReassignSection">
        <h3 className="udReassignSectionTitle">{i18n.t('logs.reassignRequestDetails') || 'Reassignment request'}</h3>

        <div className="udReassignFormRow">
          <div className="udReassignFormLabel">Reassign to</div>
          <div className="udReassignFormInputWrapper">
            <div className="udReassignStaticField">
              <span>Unique Unidentified Driver</span>
              <span style={{ fontSize: 10, color: '#666' }}>▼</span>
            </div>
          </div>
        </div>

        <div className="udReassignFormRow udReassignFormRowTextarea">
          <div className="udReassignFormLabel">Note</div>
          <div className="udReassignFormInputWrapper">
            <textarea
              className="udReassignTextarea"
              placeholder="Note"
              value={annotation}
              onChange={(e) => setAnnotation(e.target.value)}
            />
          </div>
        </div>
      </div>

      {hasReassignApiStatus?.errorMessage && (
        <Message type="error" message={i18n.t(hasReassignApiStatus.errorMessage)} />
      )}

      <div className="udReassignFooterActions">
        <button type="button" className="udReassignBtnCancel" onClick={handleClose}>
          CANCEL
        </button>
        <button
          type="button"
          className="udReassignBtnSubmit"
          disabled={!isRequestEnabled || hasReassignApiStatus?.isLoading}
          onClick={handleSubmit}
        >
          SEND REQUEST
        </button>
      </div>
    </div>
  );
};

const UdReassignmentModal = ({
  open,
  setOpen,
  entry,
  companyDrivers,
  logIdPub,
  i18n,
  createUdReassignment,
  getDailyLogDetails,
  apiStatus,
  fetchDailyLogDetailsApiId,
}) => {
  const [successOpen, setSuccessOpen] = useState(false);
  const hasReassignApiStatus = hasValue(apiStatus?.status, 'api', REASSIGN_API_ID);

  const handleClose = () => {
    setOpen(false);
  };

  const reassignFormContent = (
    <UdReassignmentFormContent
      entry={entry}
      companyDrivers={companyDrivers}
      logIdPub={logIdPub}
      i18n={i18n}
      createUdReassignment={createUdReassignment}
      getDailyLogDetails={getDailyLogDetails}
      fetchDailyLogDetailsApiId={fetchDailyLogDetailsApiId}
      hasReassignApiStatus={hasReassignApiStatus}
      handleClose={handleClose}
      setSuccessOpen={setSuccessOpen}
      setOpen={setOpen}
    />
  );

  const successModalContent = (
    <div className="successpopup">
      <SuccessIcon />
      <h2>{i18n.t('logs.udReassignSuccess')}</h2>
      <div className="deletePopupBtn">
        <div className="deletebtn">
          <Button label={i18n.t('common.ok')} onClick={() => setSuccessOpen(false)} />
        </div>
      </div>
    </div>
  );

  return (
    <Suspense fallback={null}>
      <ModalComponent
        open={open}
        setOpen={(value) => {
          if (!value) handleClose();
        }}
        isSuccess
        data={{
          content: () => reassignFormContent,
        }}
      />
      <ModalComponent
        open={successOpen}
        setOpen={setSuccessOpen}
        data={{
          content: () => successModalContent,
        }}
        isSuccess
      />
    </Suspense>
  );
};

UdReassignmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  entry: PropTypes.object,
  companyDrivers: PropTypes.object,
  logIdPub: PropTypes.string,
  i18n: PropTypes.object.isRequired,
  createUdReassignment: PropTypes.func.isRequired,
  getDailyLogDetails: PropTypes.func.isRequired,
  apiStatus: PropTypes.object,
  fetchDailyLogDetailsApiId: PropTypes.string,
};

export default UdReassignmentModal;
